import React, {useEffect, useRef} from 'react';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {IPresentation} from '../../../../incase-domain/presentation';
import useCreatePresentation from '../../../../service/hooks/useCreatePresentation';
import useImportHtmlPresentation from '../../../../service/hooks/useImportHtmlPresentation';
import useImportPptPresentation from '../../../../service/hooks/useImportPptPresentation';
import useImportPresentation from '../../../../service/hooks/useImportPresentation';
import {isHtmlFileName, readHtmlSlideFiles} from '../../../../utils/htmlSlideImport';
import {TEMPLATE} from '../../../../service/template/types';
import PresentationList from '../../../components/presentationList/PresentationList';
import PresentationsPageHeader from '../../../components/presentationList/PresentationsPageHeader';

function EditorDefaultPage(): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const createPresentationMutation = useCreatePresentation();
    const importPresentationMutation = useImportPresentation();
    const importPptPresentationMutation = useImportPptPresentation();
    const importHtmlPresentationMutation = useImportHtmlPresentation();

    const inputRef = useRef<HTMLInputElement>(null);
    const htmlInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!createPresentationMutation.isSuccess || !createPresentationMutation.data) return;

        navigate(`/presentations/${createPresentationMutation.data._id}`, {state: {template: TEMPLATE.TITLE_SUBTITLE}});
        createPresentationMutation.reset();
    }, [createPresentationMutation, navigate]);

    useEffect(() => {
        if (!importPresentationMutation.isSuccess || !importPresentationMutation.data) return;

        navigate(`/presentations/${importPresentationMutation.data._id}`);
        importPresentationMutation.reset();
    }, [importPresentationMutation, navigate]);

    useEffect(() => {
        if (!importPptPresentationMutation.isSuccess || !importPptPresentationMutation.data) return;

        navigate(`/presentations/${importPptPresentationMutation.data}`);
        importPptPresentationMutation.reset();
    }, [importPptPresentationMutation, navigate]);

    useEffect(() => {
        if (!importHtmlPresentationMutation.isSuccess || !importHtmlPresentationMutation.data) return;

        navigate(`/presentations/${importHtmlPresentationMutation.data}`);
        importHtmlPresentationMutation.reset();
    }, [importHtmlPresentationMutation, navigate]);

    const onChange = (e: FileList): void => {
        const reader = new FileReader();
        const validExtensions = ['.pptx'];
        const validPresentationExtensions = ['.json', '.nbl'];

        if (validExtensions.some((ext) => e[0].name.endsWith(ext))) {
            importPptPresentationMutation.mutate({file: e[0]});
        } else if (validPresentationExtensions.some((ext) => e[0].name.endsWith(ext))) {
            reader.onload = () => {
                importPresentationMutation.mutate({presentation: reader.result as unknown as IPresentation});
            };
            reader.readAsText(e[0]);
        } else {
            toast.error(t('editorPage.invalidFile'));
        }
    };

    const onHtmlChange = async (files: FileList): Promise<void> => {
        const selected = Array.from(files);
        if (!selected.length || selected.some((file) => !isHtmlFileName(file.name))) {
            toast.error(t('editorPage.invalidFile'));
            return;
        }

        const slides = await readHtmlSlideFiles(selected);
        if (slides.some((slide) => !slide.html.trim())) {
            toast.error(t('editorPage.emptyHtmlFile'));
            return;
        }

        importHtmlPresentationMutation.mutate({slides});
    };

    return (
        <>
            <input
                className="u-d-none"
                ref={inputRef}
                type="file"
                accept=".nbl,.pptx"
                onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                    const input = ev.currentTarget;
                    const {files} = input;
                    if (files) onChange(files);
                    input.value = '';
                }}
            />
            <input
                className="u-d-none"
                ref={htmlInputRef}
                type="file"
                accept=".html,.htm"
                multiple
                onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                    const input = ev.currentTarget;
                    const {files} = input;
                    if (files) onHtmlChange(files);
                    input.value = '';
                }}
            />
            <PresentationsPageHeader view="personal" />
            <PresentationList
                paginated
                enableFolders
                onCreatePresentation={() => createPresentationMutation.mutate(undefined)}
                onImportPresentation={() => {
                    if (inputRef.current) inputRef.current.click();
                }}
                onImportHtml={() => {
                    if (htmlInputRef.current) htmlInputRef.current.click();
                }}
            />
        </>
    );
}

export default EditorDefaultPage;
